import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../config/api';
import { formatMatchDate } from '../../utils/matchDateTime';
import { FaArrowLeft, FaClipboardList, FaEdit, FaFileExcel } from 'react-icons/fa';
import './AdminPages.css';

const CHOICE_OPTIONS = [
  { value: '', label: 'En attente' },
  { value: 'participate', label: 'Je participe au match' },
  { value: 'available', label: 'Je ne participe pas et je mets à dispo mon abo' },
  { value: 'keep', label: 'Je ne participe pas et je garde mon abo' },
];

const emptyAdminFields = () => ({
  dispositionPlace: '',
  dispositionLastName: '',
  dispositionFirstName: '',
  visitor: '',
  price: '',
});

const AdminTicketParticipationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({
    choice: '',
    adminFields: emptyAdminFields(),
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get(`/ticket-subscriber-responses/ticket/${id}`);
      setData(res.data);
    } catch (err) {
      console.error(err);
      setError('Impossible de charger les réponses');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const formatAdminDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('fr-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatSeat = (sub) => {
    const s = sub?.subscription;
    if (!s) return '—';
    const parts = [s.tribune, s.settore, s.fila, s.posto].filter(Boolean);
    return parts.length ? parts.join(' / ') : '—';
  };

  const openEdit = (row) => {
    const admin = row.adminFields || {};
    setEditingRow(row);
    setEditForm({
      choice: row.choice || '',
      adminFields: {
        dispositionPlace: admin.dispositionPlace || '',
        dispositionLastName: admin.dispositionLastName || '',
        dispositionFirstName: admin.dispositionFirstName || '',
        visitor: admin.visitor || data?.ticket?.opponentTeam || '',
        price: admin.price || '',
      },
    });
  };

  const closeEdit = () => {
    setEditingRow(null);
    setEditForm({ choice: '', adminFields: emptyAdminFields() });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    if (name === 'choice') {
      setEditForm((prev) => ({ ...prev, choice: value }));
      return;
    }
    if (name.startsWith('admin.')) {
      const key = name.slice(6);
      setEditForm((prev) => ({
        ...prev,
        adminFields: { ...prev.adminFields, [key]: value },
      }));
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingRow) return;
    try {
      setSaving(true);
      await api.patch(`/ticket-subscriber-responses/${editingRow._id}`, {
        choice: editForm.choice || null,
        adminFields: editForm.adminFields,
      });
      closeEdit();
      await load();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await api.get(
        `/ticket-subscriber-responses/ticket/${id}/export-participants`,
        { responseType: 'blob' },
      );
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = response.headers['content-disposition'];
      let filename = 'participants.xlsx';
      if (cd) {
        const m = /filename="?([^";]+)"?/i.exec(cd);
        if (m?.[1]) filename = m[1];
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Erreur lors de l\'export Excel');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <div className="loading">Chargement...</div>
        </div>
      </div>
    );
  }

  if (error || !data?.ticket) {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <p style={{ color: '#c0392b' }}>{error || 'Match introuvable'}</p>
          <button type="button" className="btn-back" onClick={() => navigate('/admin/ticket-participations')}>
            Retour
          </button>
        </div>
      </div>
    );
  }

  const { ticket, responses, stats } = data;

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-page-header">
          <button
            type="button"
            className="btn-back"
            onClick={() => navigate('/admin/ticket-participations')}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--juve-black)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '1rem',
              marginBottom: '1rem',
            }}
          >
            <FaArrowLeft /> Retour aux participations
          </button>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <h1 style={{ margin: 0 }}>
              <FaClipboardList style={{ marginRight: '0.5rem' }} />
              Réponses des abonnés
            </h1>
            <button
              type="button"
              className="btn-add"
              onClick={handleExport}
              disabled={
                exporting
                || (stats.participate ?? 0) + (stats.available ?? 0) + (stats.keep ?? 0) === 0
              }
              title="Exporter les abonnés ayant répondu (participent, abo dispo ou gardent abo)"
            >
              <FaFileExcel />
              {exporting ? 'Export…' : 'Export Excel réponses'}
            </button>
          </div>
        </div>

        <div
          style={{
            padding: '1rem 1.25rem',
            background: '#f8f9fa',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            border: '1px solid #e0e0e0',
          }}
        >
          <p style={{ margin: '0 0 4px', fontWeight: 700 }}>{ticket.matchTitle}</p>
          <p style={{ margin: 0, color: '#555' }}>{formatMatchDate(ticket.date, 'fr-FR')}</p>
          {ticket.opponentTeam && (
            <p style={{ margin: '4px 0 0', color: '#555' }}>Visiteur : {ticket.opponentTeam}</p>
          )}
          {ticket.subscriberResponseDeadline && (
            <p style={{ margin: '8px 0 0', fontSize: '0.9rem', color: '#2980b9' }}>
              Délai de réponse : <strong>{formatMatchDate(ticket.subscriberResponseDeadline, 'fr-FR')}</strong>
            </p>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap',
            marginBottom: '1.5rem',
          }}
        >
          {[
            { label: 'Total', value: stats.total },
            { label: 'Réponses', value: stats.responded },
            { label: 'En attente', value: stats.pending },
            { label: 'Participent', value: stats.participate },
            { label: 'Abo dispo', value: stats.available },
            { label: 'Gardent abo', value: stats.keep },
          ].map((s) => (
            <div
              key={s.label}
              style={{
                padding: '0.75rem 1rem',
                background: '#fff',
                border: '1px solid #ddd',
                borderRadius: '6px',
                minWidth: '100px',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{s.value}</div>
              <div style={{ fontSize: '0.8rem', color: '#666' }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>E-mail</th>
                <th>Place</th>
                <th>Réponse</th>
                <th>Disposition</th>
                <th>Visiteur</th>
                <th>Prix</th>
                <th>Répondu le</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {responses.map((row) => {
                const sub = row.user || {};
                const admin = row.adminFields || {};
                const disposition =
                  [admin.dispositionPlace, admin.dispositionLastName, admin.dispositionFirstName]
                    .filter(Boolean)
                    .join(' — ') || '—';
                return (
                  <tr key={row._id}>
                    <td>
                      {sub.firstName} {sub.lastName}
                    </td>
                    <td>{sub.email}</td>
                    <td>{formatSeat(sub)}</td>
                    <td>
                      {row.choiceLabel ? (
                        <span
                          style={{
                            fontSize: '0.85rem',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            background:
                              row.choice === 'participate'
                                ? '#d4edda'
                                : row.choice === 'available'
                                  ? '#cce5ff'
                                  : row.choice === 'keep'
                                    ? '#f8d7da'
                                    : '#f0f0f0',
                          }}
                        >
                          {row.choiceLabel}
                        </span>
                      ) : (
                        <span style={{ color: '#999' }}>En attente</span>
                      )}
                    </td>
                    <td style={{ fontSize: '0.85rem', maxWidth: '160px' }}>{disposition}</td>
                    <td>{admin.visitor || '—'}</td>
                    <td>{admin.price || '—'}</td>
                    <td>{formatAdminDate(row.respondedAt)}</td>
                    <td className="actions">
                      <button
                        type="button"
                        className="btn-edit"
                        onClick={() => openEdit(row)}
                        aria-label="Modifier la ligne"
                        title="Modifier"
                      >
                        <FaEdit />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {responses.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', color: '#999' }}>
                    Aucune réponse enregistrée
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {editingRow && (
          <div className="modal-overlay" onClick={closeEdit}>
            <div
              className="modal-content"
              style={{ maxWidth: '640px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2>
                Modifier — {editingRow.user?.firstName} {editingRow.user?.lastName}
              </h2>
              <form onSubmit={handleSaveEdit} className="admin-form">
                <div className="form-group">
                  <label htmlFor="edit-choice">Participation au match</label>
                  <select
                    id="edit-choice"
                    name="choice"
                    value={editForm.choice}
                    onChange={handleEditChange}
                  >
                    {CHOICE_OPTIONS.map((opt) => (
                      <option key={opt.value || 'pending'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <h3 style={{ margin: '1.25rem 0 0.75rem', fontSize: '1rem' }}>Disposition / export</h3>

                <div className="form-group">
                  <label htmlFor="edit-dispositionPlace">Disposition Place</label>
                  <input
                    id="edit-dispositionPlace"
                    name="admin.dispositionPlace"
                    type="text"
                    value={editForm.adminFields.dispositionPlace}
                    onChange={handleEditChange}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label htmlFor="edit-dispositionLastName">Nom (disposition)</label>
                    <input
                      id="edit-dispositionLastName"
                      name="admin.dispositionLastName"
                      type="text"
                      value={editForm.adminFields.dispositionLastName}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-dispositionFirstName">Prénom (disposition)</label>
                    <input
                      id="edit-dispositionFirstName"
                      name="admin.dispositionFirstName"
                      type="text"
                      value={editForm.adminFields.dispositionFirstName}
                      onChange={handleEditChange}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label htmlFor="edit-visitor">Visiteur</label>
                    <input
                      id="edit-visitor"
                      name="admin.visitor"
                      type="text"
                      value={editForm.adminFields.visitor}
                      onChange={handleEditChange}
                      placeholder={ticket.opponentTeam || ''}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-price">Prix</label>
                    <input
                      id="edit-price"
                      name="admin.price"
                      type="text"
                      value={editForm.adminFields.price}
                      onChange={handleEditChange}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-submit" disabled={saving}>
                    {saving ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                  <button type="button" className="btn-cancel" onClick={closeEdit} disabled={saving}>
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTicketParticipationDetail;
